/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
// import { UUID } from "crypto";
import { DocumentResponse } from "@interfaces/response";
import { ApiError } from "@interfaces/response";
import { DOCUMENTS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
// import { BankInformationPayload } from "@interfaces/SecondGuarantee";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { ProgressBar } from "@components/ProgressBar";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useDeleteBankAccountMutation,
  useDeleteBankAccountStatementMutation,
  useLazyGetBankAccountsQuery,
  useLazyGetLoanApplicationByIdQuery,
  usePostBankAccountsMutation,
  usePostBankAccountStatementMutation,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { identifyBankAccounts } from "@helpers/funciones";
import { useLazyGetDocumentAppIdQuery, usePatchDocumentMutation } from "@api/documents";
import { BankAccountFormItem, PayloadBankAccount } from "@interfaces/index";
import { CustomDialog } from "@components/CustomDialog";
import { setLoanStatus } from "@store/slices/registerSlice";
import { useNavigate } from "react-router-dom";

// Generador de cuenta bancaria por defecto alineado a BankAccountFormItem (usa 'statements')
const createDefaultBankAccount = (numberOfBankStatements: number): BankAccountFormItem => ({
  id: "",
  clabe: "000000000000000000",
  bank_id: "9f94e186-4aba-4810-b5c3-e5942393b228",
  bank_name: "BANCO NO VALIDO",
  statements: Array.from({ length: numberOfBankStatements }, (_, index) => ({
    bank_statement_document_id: undefined,
    document_id: "",
    document_order: index + 1,
    document_value: undefined,
  })),
});
const SCREEN_STATUS = "CSIBA";

const DEFAULT_NUMBER_OF_BANK_STATEMENTS = 3;

const normalizeStatements = (
  statements: Array<BankAccountFormItem["statements"][number] | undefined> | undefined,
  targetCount: number,
): BankAccountFormItem["statements"] => {
  const current = Array.isArray(statements) ? [...statements] : [];
  return Array.from({ length: targetCount }, (_, index) => {
    const existing = current[index];
    if (existing) {
      return {
        bank_statement_document_id: existing.bank_statement_document_id,
        document_id: existing.document_id ?? "",
        document_order: index + 1,
        document_value: existing.document_value,
      };
    }

    return {
      bank_statement_document_id: undefined,
      document_id: "",
      document_order: index + 1,
      document_value: undefined,
    };
  });
};

const InformacionBancaria = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [triggerPostBankAccountStatement] = usePostBankAccountStatementMutation();
  const [triggerDeleteBankAccountStatement] = useDeleteBankAccountStatementMutation();
  const [deleteBankAccount] = useDeleteBankAccountMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [loadingBankStatement, setLoadingBankStatement] = useState<boolean>(false);
  const [accountIndexToDelete, setAccountIndexToDelete] = useState<number | undefined>(undefined);
  const [codeStatus, setCodeStatus] = useState<string>("");

  const numberOfBankStatements = useMemo(() => {
    if (!interfaceConfig.formularios) return DEFAULT_NUMBER_OF_BANK_STATEMENTS;

    switch (applicationData.tax_system_type) {
      case TAX_SYSTEM_TYPE.PFAE.code:
        return (
          interfaceConfig?.formularios?.informacion_bancaria_arredanmiento?.pfae.numero_estados_cuenta ||
          interfaceConfig?.formularios?.informacion_bancaria_empresa?.numero_estados_cuenta ||
          DEFAULT_NUMBER_OF_BANK_STATEMENTS
        );

      case TAX_SYSTEM_TYPE.PF.code:
        return (
          interfaceConfig?.formularios?.informacion_bancaria_arredanmiento?.pf.numero_estados_cuenta ||
          interfaceConfig?.formularios?.informacion_bancaria_empresa?.numero_estados_cuenta ||
          DEFAULT_NUMBER_OF_BANK_STATEMENTS
        );

      case TAX_SYSTEM_TYPE.PM.code:
        return (
          interfaceConfig?.formularios?.informacion_bancaria_empresa?.numero_estados_cuenta ||
          DEFAULT_NUMBER_OF_BANK_STATEMENTS
        );

      default:
        return DEFAULT_NUMBER_OF_BANK_STATEMENTS;
    }
  }, [applicationData.tax_system_type, interfaceConfig.formularios]);

  // Evita crear la cuenta inicial más de una vez (por ejemplo, por el doble render de StrictMode)
  const isCreatingInitialAccount = useRef(false);

  const emptyBankAccount: BankAccountFormItem = createDefaultBankAccount(numberOfBankStatements);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        accounts: yup.array().of(
          yup.object().shape({
            statements: yup
              .array()
              .of(
                yup.object().shape({
                  document_id: yup.string().required("Documento requerido."),
                  document_order: yup.number().required().min(1).max(numberOfBankStatements),
                }),
              )
              .min(1, "Debes cargar al menos un documento."),
          }),
        ),
      }),
    [numberOfBankStatements],
  );

  const formik = useFormik({
    initialValues: {
      accounts: [{ ...emptyBankAccount }],
    },
    validationSchema,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        void values;
        setSubmitting(true);

        if (codeStatus === SCREEN_STATUS) {
          await triggerPostNextStage({
            applicationId: applicationData.id,
            statusCode: nextStatus?.code || "",
            username: `TOFU - ${accountUser.email}`,
          }).unwrap();
        }

        dispatch(setLoanStatus(String(nextStatus?.code)));
        navigate(String(nextStatus?.url || ""));
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaAval-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    // IMPORTANTE: no re-inicializar al cambiar config (3→6) para no perder `account.id`
    enableReinitialize: false,
    validateOnMount: true,
  });

  // Cuando cambia el número requerido de estados, ajustar la longitud de `statements`
  // preservando IDs y documentos ya cargados.
  useEffect(() => {
    const needsResize = formik.values.accounts.some(
      (account) => (account?.statements?.length || 0) !== numberOfBankStatements,
    );
    if (!needsResize) return;

    const resizedAccounts: BankAccountFormItem[] = formik.values.accounts.map((account) => ({
      ...account,
      statements: normalizeStatements(account.statements, numberOfBankStatements),
    }));

    formik.setFieldValue("accounts", resizedAccounts, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfBankStatements]);

  const handleAddAccount = async () => {
    if (formik.values.accounts.length >= 3) {
      return;
    }
    formik.setFieldValue("accounts", [...formik.values.accounts, createDefaultBankAccount(numberOfBankStatements)]);
    await createAccounts(formik.values.accounts.length);
  };

  const getInitialData = async () => {
    try {
      setIsLoading(true);

      const [list_bank_accounts, documents, loan_application] = await Promise.all([
        getBankAccounts(applicationData.id).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          clientDetailsId: personalData.id,
          status: true,
        }).unwrap(),
        getLoanApplication(applicationData.id).unwrap(),
      ]);

      setCodeStatus(loan_application?.status_catalog?.code || "");
      const { company_accounts } = identifyBankAccounts({
        bank_accounts: list_bank_accounts,
        company: personalData,
      });

      const accounts: BankAccountFormItem[] = [];
      const num_statements_required = numberOfBankStatements;

      for (let index = 0; index < company_accounts.length; index++) {
        const account = company_accounts[index];

        const new_accout: BankAccountFormItem = {
          id: account.id,
          clabe: account.clabe || "",
          bank_id: account?.bank_id || "",
          bank_name: account?.bank_name || "",
          statements: normalizeStatements(undefined, num_statements_required),
        };

        for (let statementIndex = 0; statementIndex < num_statements_required; statementIndex++) {
          const account_document = account.documents[statementIndex];
          if (account_document) {
            new_accout.statements[statementIndex] = {
              bank_statement_document_id: account_document.bank_statement_document_id,
              document_id: account_document.document_id,
              document_order: account_document.document_order || statementIndex + 1,
              document_value: documents.find((item) => item.id === account_document.document_id),
            };
          }
        }

        accounts.push(new_accout);
      }

      if (accounts.length) {
        formik.setFieldValue("accounts", accounts);
      } else {
        await createInitialAccounts();
      }
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancaria-initial_fetch",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const createInitialAccounts = async (index = 0) => {
    try {
      if (isCreatingInitialAccount.current) {
        return;
      }
      isCreatingInitialAccount.current = true;
      const payload: PayloadBankAccount = {
        loan_application_id: String(applicationData.id),
        client_details_id: personalData.id,
        account: 0,
        account_holder: "",
        person_id: null,
        clabe: "000000000000000000",
        bank_id: "9f94e186-4aba-4810-b5c3-e5942393b228",
        documents: [],
      };
      const response = await triggerPostBankAccount({
        solicitudId: applicationData.id,
        body: payload,
      }).unwrap();

      formik.setFieldValue(`accounts[${index}].id`, response.id);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancaria-createInitialAccounts",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      isCreatingInitialAccount.current = false;
    }
  };

  const createAccounts = async (index = 0) => {
    try {
      setIsLoading(true);
      const payload: PayloadBankAccount = {
        loan_application_id: String(applicationData.id),
        client_details_id: personalData.id,
        account: 0,
        account_holder: "",
        person_id: null,
        clabe: `000000000000000000${index + 1}`,
        bank_id: "9f94e186-4aba-4810-b5c3-e5942393b228",
        documents: [],
      };
      const response = await triggerPostBankAccount({
        solicitudId: applicationData.id,
        body: payload,
      }).unwrap();

      formik.setFieldValue(`accounts[${index}].id`, response.id);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancaria-createInitialAccounts",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadBankStatement = async (
    newUpload: DocumentResponse | undefined,
    accountIndex: number,
    statementIndex: number,
  ) => {
    try {
      setLoadingBankStatement(true);

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account: BankAccountFormItem = copy_accounts[accountIndex];
      const statement = account.statements[statementIndex];

      if (newUpload) {
        if (account.id) {
          const response = await triggerPostBankAccountStatement({
            solicitudId: applicationData.id,
            accountId: account.id,
            body: {
              loan_application_bank_account_id: account.id,
              document_id: newUpload.id,
              document_order: statementIndex + 1,
            },
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: response.id,
            document_id: newUpload.id,
            document_order: statementIndex + 1,
            document_value: newUpload,
          };

          // actualizar el formulario
          formik.setFieldValue("accounts", copy_accounts);
        }
      } else {
        if (account.id && statement?.bank_statement_document_id) {
          await triggerDeleteBankAccountStatement({
            solicitudId: applicationData.id,
            accountId: account.id,
            documentId: statement.bank_statement_document_id,
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: undefined,
            document_id: "",
            document_order: statementIndex + 1,
            document_value: undefined,
          };

          // actualizar el formulario
          formik.setFieldValue("accounts", copy_accounts);
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaEmpresa-handleUploadBankStatement",
        },
      });
    } finally {
      setLoadingBankStatement(false);
    }
  };

  const handleOpenModalDelete = (index: number) => {
    setAccountIndexToDelete(index);
  };

  const handleOnCloseModalDelete = () => {
    setAccountIndexToDelete(undefined);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      if (accountIndexToDelete === undefined) return;

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account_to_delete = copy_accounts[accountIndexToDelete];

      if (!account_to_delete || !account_to_delete.id) {
        const new_accounts = copy_accounts.filter((_, index) => index !== accountIndexToDelete);
        formik.setFieldValue("accounts", new_accounts);
        setAccountIndexToDelete(undefined);
        return;
      }

      // verificar si hay documentos validados relacionados a la cuenta bancaria
      const documents_validated = account_to_delete.statements.filter(
        (item) => item?.document_value?.validated === true,
      );

      if (documents_validated.length > 0) {
        // dispatch(setModalErrorVisible({ open: true, error: {
        //   message: "No se puede eliminar la cuenta bancaria porque hay documentos validados relacionados.",
        // } }));
        console.error("no se puede eliminar la cuenta bancaria porque hay documentos validados relacionados.");
        return;
      }

      // desactivar documentos
      for (let index = 0; index < account_to_delete.statements.length; index++) {
        const bank_statement = account_to_delete.statements[index];
        if (bank_statement && bank_statement.document_id) {
          await triggerPatchDocument({
            documentId: bank_statement.document_id as `${string}-${string}-${string}-${string}-${string}`,
            body: {
              status: false,
            },
          }).unwrap();
        }
      }

      // desactivar la cuenta bancaria
      await deleteBankAccount({
        solicitudId: applicationData.id,
        accountId: account_to_delete.id,
      }).unwrap();

      const new_accounts = copy_accounts.filter((_, index) => index !== accountIndexToDelete);
      formik.setFieldValue("accounts", new_accounts);
      handleOnCloseModalDelete();
    } catch (error) {
      console.error("Ha ocurrido un error al eliminar la cuenta bancaria", error);
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaEmpresa-handleConfirmDelete",
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información bancaria
          </Typography>

          <Container maxWidth="sm" sx={{ mt: 2 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.dark}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={300}
            mt={2}
            mb={4}
          >
            Ingresa los últimos {numberOfBankStatements} estados de cuenta bancarios de la cuenta en dónde concentras el mayor ingreso de tu
            negocio
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            {formik.values.accounts.map((account: BankAccountFormItem, index: number) => (
              <Box key={index} mt={index > 0 ? 4 : 0}>
                <Typography
                  textAlign={"center"}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  fontWeight={700}
                  color={theme?.palette?.primary?.dark}
                >
                  Cuenta Bancaria {index + 1}
                </Typography>

                {account.statements.map((statement, statementIndex) => (
                  <Box key={`bank_statement_${index}_${statementIndex}`}>
                    <CustomUploadFileV2
                      mt={2}
                      inputName={`bank_statement_${index}_${statementIndex}`}
                      uploadedFile={statement?.document_value}
                      setUploadedFile={(newUpload) => {
                        handleUploadBankStatement(newUpload, index, statementIndex);
                      }}
                      optionalName={`Estado de cuenta ${statementIndex + 1}`}
                      optionalDescription=""
                      metadata={{
                        fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                        uploadType: "client",
                        clientDetailsId: personalData.id,
                      }}
                      disabled={isLoading || isDeleting || loadingBankStatement || formik.isSubmitting || !account.id}
                      required
                    />
                  </Box>
                ))}
                {index > 0 && (
                  <Typography
                    onClick={() => handleOpenModalDelete(index)}
                    color={theme?.palette?.primary?.main}
                    fontSize={"15px"}
                    fontWeight={400}
                    mt={3}
                    sx={{
                      cursor:
                        isLoading || isDeleting || formik.values.accounts[index].id === undefined
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    - Eliminar cuenta bancaria
                  </Typography>
                )}
              </Box>
            ))}

            <Box
              mt={3}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: theme?.palette?.primary?.base,
                padding: "10px 12px",
                borderRadius: "8px",
                paddingLeft: { xs: "12px", sm: "26px" },
                paddingRight: { xs: "12px", sm: "106px" },
              }}
            >
              <WarningAmberIcon
                sx={{
                  color: theme?.palette?.primary?.light,
                  fontSize: 40,
                }}
              />

              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme?.palette?.primary?.light,
                  lineHeight: 1.4,
                }}
                textAlign={"justify"}
                pl={2}
              >
                Si la cuenta refleja menos del 60% de los ingresos operativos, agrega otra cuenta bancaria hasta llegar
                a dicho porcentaje.
              </Typography>
            </Box>

            <Typography
              onClick={handleAddAccount}
              color={theme?.palette?.primary?.main}
              fontSize={"15px"}
              fontWeight={400}
              mt={3}
              sx={{
                cursor: formik.values.accounts.length >= 3 ? "not-allowed" : "pointer",
              }}
            >
              + Agregar otra cuenta bancaria
            </Typography>

            <Box display="flex" justifyContent="center" mt={4} mb={4}>
              <Button type="submit" variant="default" disabled={isLoading || !formik.isValid || formik.isSubmitting}>
                Continuar
              </Button>
            </Box>
          </form>
        </Box>

        <CustomDialog
          colorClose={theme?.palette?.primary?.light}
          open={accountIndexToDelete !== undefined}
          onClose={handleOnCloseModalDelete}
          itemTitle={<></>}
          itemContent={
            <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
              <Typography
                textAlign={"center"}
                color={theme?.palette?.primary?.dark}
                fontWeight={600}
                variant={isMobileOrTablet ? "h5" : "h6"}
              >
                ¿Estás seguro de eliminar la cuenta bancaria?
              </Typography>
              <Box display={"flex"} columnGap={2} justifyContent={"center"} mt={3}>
                <Button
                  variant="blue_outlined"
                  sx={{ py: 0, px: 1, width: "110px" }}
                  onClick={handleOnCloseModalDelete}
                  disabled={isDeleting}
                >
                  Regresar
                </Button>

                <Button
                  variant="default"
                  sx={{ width: "110px" }}
                  onClick={() => handleConfirmDelete()}
                  disabled={isDeleting}
                >
                  Eliminar
                </Button>
              </Box>
            </Box>
          }
        />
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};
export default InformacionBancaria;
